import { useMemo } from 'react';
import dayjs from 'dayjs';
import { findDatesRange, useTimePeriodPopoverSelector } from '@frontend/analytics-shared';
import { TFunction, useTranslation } from '@frontend/i18n';

interface ScheduleRequestTimePeriodFilterProps {
  startDate?: string;
  endDate?: string;
  onChange: (dates: { startDate: string; endDate: string }) => void;
}

type TimePeriodOption = { label: string; value: { startDate: string; endDate: string } };

export type ScheduleRequestTimePeriods = {
  next7Days: TimePeriodOption;
  next15Days: TimePeriodOption;
  last7Days: TimePeriodOption;
  last30Days: TimePeriodOption;
};

export const getScheduleRequestTimePeriods = (t: TFunction): ScheduleRequestTimePeriods => ({
  next7Days: { label: t('Next 7 Days'), value: findDatesRange({ offset: 7, range: 'day', type: 'future' }) },
  next15Days: { label: t('Next 15 Days'), value: findDatesRange({ offset: 15, range: 'day', type: 'future' }) },
  last7Days: { label: t('Last 7 Days'), value: findDatesRange({ offset: 7, range: 'day', type: 'past' }) },
  last30Days: { label: t('Last 30 Days'), value: findDatesRange({ offset: 30, range: 'day', type: 'past' }) },
});

export const ScheduleRequestTimePeriodFilter = ({
  startDate,
  endDate,
  onChange,
}: ScheduleRequestTimePeriodFilterProps) => {
  const { t } = useTranslation('analytics');

  const timePeriods = useMemo(() => getScheduleRequestTimePeriods(t), [t]);

  const { TimePeriodPopoverSelector } = useTimePeriodPopoverSelector<ScheduleRequestTimePeriods>({
    defaultPeriod: 'last30Days',
    bypassMinDateFilter: true,
    timePeriods,
    values: {
      startDate,
      endDate,
      selectedPeriod: undefined,
    },
  });

  return (
    <TimePeriodPopoverSelector
      onChange={({ startDate: sDate, endDate: eDate }) => {
        if (sDate && eDate) {
          onChange({
            startDate: dayjs(sDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
            endDate: dayjs(eDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
          });
        }
      }}
      persistDefaultLabel
      trackingIdBase='schedule-requests-time-period'
      label={t('Time Period')}
    />
  );
};
